import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { asChakraVariable } from '@mangovoice/design-system'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

import { Radii } from '../../../types/ThemeTypes'
import { hiddenRowActionStyle, revealedRowActionStyle } from './rowActionReveal'

// Using asChakraVariable for boxShadow values that need CSS variables

export enum CallContactButtonMenuVariants {
  Fixed = 'fixed',
  Scrollable = 'scrollable',
}

const CallContactButtonMenuParts = [
  'container',
  'itemContainer',
  'itemText',
  'itemBadge',
] as const

const menuHelpers = createMultiStyleConfigHelpers(CallContactButtonMenuParts)
export const CallContactButtonMenu = menuHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      flexDirection: 'column',
      padding: '0.25rem',
      borderRadius: '0.38rem',
      gap: '0.5rem',
      backgroundColor: 'bg.panel',
      boxShadow: asChakraVariable({ default: 'shadows.default' }),
    },
    itemContainer: {
      alignItems: 'center',
      padding: '0.5rem',
      gap: '0.5rem',
      _hover: { backgroundColor: 'bg.muted' },
      _focusVisible: {
        backgroundColor: 'bg.muted',
        outline: '2px solid',
        outlineColor: 'brand.fg',
        outlineOffset: '-2px',
      },
      cursor: 'pointer',
    },
    itemText: {
      ...textStyles[textStyleKeys.Body1],
    },
    itemBadge: {
      backgroundColor: 'bg.muted',
      color: 'brand.contrast',
      ...textStyles[textStyleKeys.Body4_Bold],
    },
  },
  variants: {
    [CallContactButtonMenuVariants.Fixed]: {},
    [CallContactButtonMenuVariants.Scrollable]: {
      container: {
        // We want to show 5 (to match max fixed) and scroll for more
        // 15rem was a lucky guess
        maxHeight: '15rem',
        overflowY: 'scroll',
      },
    },
  },
  defaultProps: {
    variant: CallContactButtonMenuVariants.Fixed,
  },
})

export const useCallContactButtonMenuStyles = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(CallContactButtonMenu, props) as MultiStyleType<
    typeof CallContactButtonMenuParts
  >
}

export const CALL_CONTACT_BUTTON_CLASS_NAME = 'contact-button'

const focusRing = {
  outline: '2px solid',
  outlineColor: 'brand.fg',
  outlineOffset: '2px',
}

const CallContactButtonParts = [
  'container',
  'callButton',
  'callButtonIcon',
  'callButtonText',
  'menuButton',
  'menuButtonIconContainer',
  'menuButtonIcon',
] as const

const buttonHelpers = createMultiStyleConfigHelpers(CallContactButtonParts)
export const CallContactButton = buttonHelpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'flex',
      flexShrink: 0,
      gap: '0.06rem',
      borderRadius: '3.125rem',
      '& > button:first-of-type': {
        borderRadius: '3.125rem 0rem 0rem 3.125rem',
      },
      '& > button:last-of-type': {
        borderRadius: '0rem 3.125rem 3.125rem 0rem',
      },
      '& > button:only-of-type': { borderRadius: '3.125rem' },
      '&:hover': {
        boxShadow: asChakraVariable({ default: 'shadows.focus' }),
      },
      // Hidden until its row is hovered or focused (see ContactCard.style)
      ...hiddenRowActionStyle,
      '&[data-menu-open]': revealedRowActionStyle,
    },
    callButton: {
      justifyContent: 'center',
      alignItems: 'center',
      gap: '0.38rem',
      borderRadius: Radii.md,
      backgroundColor: 'brand.solid',
      paddingY: '0.5rem',
      paddingX: '0.75rem',
      cursor: 'pointer',
      _hover: {
        backgroundColor: 'brand.solid',
      },
      _active: {
        backgroundColor: 'brand.emphasized',
        boxShadow: asChakraVariable({ default: 'shadows.focus' }),
      },
      _focusVisible: focusRing,
    },
    callButtonIcon: {
      boxSize: '1rem',
      color: 'brand.contrast',
    },
    callButtonText: {
      ...textStyles[textStyleKeys.Body2_Semibold],
      color: 'brand.contrast',
    },
    menuButton: {
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'brand.solid',
      cursor: 'pointer',
      _hover: {
        backgroundColor: 'brand.solid',
      },
      _active: {
        backgroundColor: 'brand.emphasized',
        boxShadow: asChakraVariable({ default: 'shadows.focus' }),
      },
      _focusVisible: focusRing,
    },
    menuButtonIconContainer: {
      boxSize: 'full',
      alignItems: 'center',
      justifyContent: 'center',
      paddingY: '0.5rem',
      paddingRight: '0.75rem',
      paddingLeft: '0.5rem',
    },
    menuButtonIcon: {
      boxSize: '0.75rem',
      color: 'brand.contrast',
    },
  },
})

export const useCallContactButtonStyles = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(CallContactButton, props) as MultiStyleType<
    typeof CallContactButtonParts
  >
}
